/**
 * WebDialerReceiveIndicator — status pill + minimal menu for THIS TAB's inbound-call registration.
 *
 * The pill answers "is this tab going to ring?" at a glance; clicking it opens a deliberately
 * MINIMAL menu (owner decision — the full routing editor lives in Settings → VoIP):
 *   - who is receiving right now (org-wide live tabs, when the caller may read them),
 *   - stop/resume receiving on THIS tab,
 *   - (admins) a shortcut to the routing settings.
 * Renders nothing while receive mode is off, so it costs no corner space for most users.
 *
 * POLLS the manager (5s) instead of subscribing: registration health is derived from several
 * fields across the connect state machine, and wiring events through every transition would
 * touch paths that many review rounds hardened. A ≤5s-stale pill is fine.
 *
 * Rendered by VoiceCallProvider (desktop web, no call UI on screen), bottom-right corner.
 */

import { useEffect, useRef, useState } from 'react';
import { PhoneIncoming, PhoneOff, RefreshCw, Settings, ShieldOff } from 'lucide-react';
import { telnyxVoiceWebManager } from '@/lib/telnyxVoiceWeb';
import { voiceManager } from '@/lib/voiceManager';
import { apiService } from '@/lib/api';
import { navigateApp } from '@/lib/navigation';
import { usePermissions } from '@/hooks/usePermissions';
import { isDesktopWeb, resyncWebDialerRegistration } from '@/hooks/useWebDialerRegistration';

type ReceiveStatus = 'off' | 'registered' | 'reconnecting' | 'blocked' | 'paused' | 'remote-disconnected';

const POLL_MS = 5_000;

// Dark text on solid light pill backgrounds. Deliberately NO dark: variants (owner feedback,
// twice): the app has no darkMode config, so Tailwind's default 'media' strategy applies them
// whenever the USER'S OS is dark — and the dark variants (bright -300 text over a translucent
// background) rendered as bright-on-light exactly where the owner was testing. The pill brings
// its own solid light background, so the fixed dark-on-light styling reads everywhere.
const RENDER: Record<Exclude<ReceiveStatus, 'off'>, {
  icon: typeof PhoneIncoming; label: string; title: string; className: string; pulse?: boolean;
}> = {
  registered: {
    icon: PhoneIncoming,
    label: 'Receiving calls',
    title: 'Incoming calls to your company number ring in this tab. Click for options.',
    className: 'border-green-400 bg-green-50 text-green-900',
  },
  reconnecting: {
    icon: RefreshCw,
    label: 'Connecting...',
    title: "Restoring this tab's incoming-call connection. Calls may not ring here until it finishes.",
    className: 'border-amber-400 bg-amber-50 text-amber-900',
    pulse: true,
  },
  blocked: {
    icon: PhoneOff,
    label: 'Not receiving — tab limit',
    title: 'You already have the maximum number of tabs receiving calls. Close another tab; this one retries automatically every 2 minutes.',
    className: 'border-red-400 bg-red-50 text-red-900',
  },
  paused: {
    icon: PhoneOff,
    label: 'Paused on this tab',
    title: 'You stopped receiving calls on this tab. Click to resume.',
    className: 'border-gray-400 bg-gray-100 text-gray-800',
  },
  'remote-disconnected': {
    icon: ShieldOff,
    label: 'Disconnected',
    title: 'This tab was disconnected from receiving calls (by an administrator, or from another of your tabs). Reload the page to register it again.',
    className: 'border-gray-400 bg-gray-100 text-gray-800',
  },
};

interface LiveReceiver { userId: string; name: string; tabs: number }

export function WebDialerReceiveIndicator() {
  const { isFeatureAllowed } = usePermissions();
  const [status, setStatus] = useState<ReceiveStatus>('off');
  const [open, setOpen] = useState(false);
  /** null = list unavailable (no voip.inbound-routing.read, or transient failure) — hide it. */
  const [receivers, setReceivers] = useState<LiveReceiver[] | null>(null);
  /** Bumped by Stop/Resume so the open menu re-fetches instead of showing pre-action data. */
  const [refreshTick, setRefreshTick] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDesktopWeb()) return;
    const read = () => setStatus(telnyxVoiceWebManager.receiveStatus);
    read();
    const timer = setInterval(read, POLL_MS);
    return () => clearInterval(timer);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // While the menu stays open, keep the receivers list current (another tab closing is reaped
  // server-side within moments — the open list should follow without a close/reopen).
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setRefreshTick((t) => t + 1), POLL_MS);
    return () => clearInterval(timer);
  }, [open]);

  // Load "who is receiving" when the menu opens, and again after Stop/Resume (refreshTick — the
  // list must reflect the action without closing and reopening the menu). Best-effort: plain
  // receivers may lack the routing-read permission these endpoints require — they just don't
  // get the list.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const [live, targets] = await Promise.all([
          apiService.getInboundRoutingLiveSessions(),
          apiService.getInboundRoutingTargets(),
        ]);
        if (cancelled) return;
        const nameById = new Map(targets.items.map((t) => [t.id, t.name || t.email || `${t.id.slice(0, 8)}…`]));
        setReceivers(live.items.map((i) => ({
          userId: i.userId,
          name: nameById.get(i.userId) ?? `${i.userId.slice(0, 8)}…`,
          tabs: i.sessionCount,
        })));
      } catch {
        if (!cancelled) setReceivers(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, refreshTick]);

  if (status === 'off') return null;
  const r = RENDER[status];
  const Icon = r.icon;
  const canPause = status === 'registered' || status === 'reconnecting' || status === 'blocked';

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-40">
      {open && (
        <div className="absolute bottom-11 right-0 w-72 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Receiving now</p>
          {receivers === null && (
            <p className="mt-1 text-xs text-gray-500">List unavailable.</p>
          )}
          {receivers !== null && receivers.length === 0 && (
            <p className="mt-1 text-xs text-gray-500">No browsers are currently receiving.</p>
          )}
          {receivers !== null && receivers.length > 0 && (
            <ul className="mt-1.5 max-h-40 space-y-1 overflow-y-auto">
              {receivers.map((u) => (
                <li key={u.userId} className="flex items-center justify-between gap-2 text-sm text-gray-800 dark:text-gray-200">
                  <span className="truncate">{u.name}</span>
                  <span className="flex-shrink-0 text-xs text-gray-500">{u.tabs} tab{u.tabs === 1 ? '' : 's'}</span>
                </li>
              ))}
            </ul>
          )}
          {receivers !== null && (
            // Expectation-setting (owner request): tab counts are heartbeat-based, so a closed
            // tab whose cleanup was missed can linger up to the backend's 5-minute liveness
            // window before it clears. Keep "5 minutes" in sync with LivenessWindow.
            <p className="mt-1.5 text-[11px] leading-snug text-gray-400">
              A closed tab can stay listed for up to 5 minutes before it clears.
            </p>
          )}

          <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 dark:border-gray-700">
            {canPause && (
              <button
                onClick={() => {
                  void (async () => {
                    // pauseInboundReceive resolves after the session DELETE, so a refetch right
                    // after shows this tab already gone from the list — no close/reopen needed.
                    await voiceManager.pauseInboundReceive();
                    setStatus(telnyxVoiceWebManager.receiveStatus);
                    setRefreshTick((t) => t + 1);
                  })();
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Stop receiving in this tab
              </button>
            )}
            {status === 'paused' && (
              <button
                onClick={() => {
                  voiceManager.resumeInboundReceive();
                  resyncWebDialerRegistration();
                  setStatus(telnyxVoiceWebManager.receiveStatus);
                  setRefreshTick((t) => t + 1);
                  // The fresh session appears server-side a moment later — refresh once more so
                  // the reopened registration shows up without closing the menu.
                  setTimeout(() => setRefreshTick((t) => t + 1), 2000);
                }}
                className="w-full rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
              >
                Resume receiving in this tab
              </button>
            )}
            {status === 'registered' && (
              <button
                onClick={() => {
                  void (async () => {
                    // Fallback for stale sessions (e.g. an undelivered tab-close beacon): end
                    // every OTHER tab of THIS user, server-side. This tab's session is passed as
                    // `keep` and survives; other live tabs latch off until reloaded.
                    const keep = telnyxVoiceWebManager.currentReceiveSessionId;
                    if (!keep) return;
                    try { await apiService.endOtherWebDialerSessions(keep); } catch { /* best effort */ }
                    setRefreshTick((t) => t + 1);
                  })();
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Disconnect my other tabs
              </button>
            )}
            {status === 'remote-disconnected' && (
              <p className="text-xs text-gray-500">Reload the page to receive calls again.</p>
            )}
            {isFeatureAllowed('voip.admin') && (
              <button
                onClick={() => { setOpen(false); navigateApp('/settings?tab=voip'); }}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Settings className="h-3.5 w-3.5" />
                Routing settings
              </button>
            )}
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        title={r.title}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-md transition-shadow hover:shadow-lg ${r.className}`}
      >
        <Icon className={`h-3.5 w-3.5 ${r.pulse ? 'animate-spin' : ''}`} />
        {r.label}
      </button>
    </div>
  );
}
